import React from 'react';
import { useField } from 'formik';
import * as Yup from 'yup';
import { Box, FormHelperText, Grid, InputLabel, Typography } from '@mui/material';
import { Article } from '@mui/icons-material';
import { DatePicker } from '../../Inputs/DatePicker';
import { TextInput } from '../../Inputs/TextInput';
import { MaskedInput } from '../../Inputs/MaskedInput';
import { SimpleDropzone } from '../../Dropzone';
import { MAX_PDF_UPLOAD_SIZE } from '@uplift/constants';

export const collabAgreementValidationSchema = Yup.object().shape({
  collaborationAgreement: Yup.mixed().required('Please upload a collaboration agreement'),
  expirationDate: Yup.string().required('Expiration date is required'),
  physicianFirstName: Yup.string().required('Physician first name is required'),
  physicianLastName: Yup.string().required('Physician last name is required'),
  physicianNpi: Yup.string().required('NPI is required'),
  physicianEmail: Yup.string().email('Invalid email address').required('Email is required'),
  physicianPhone: Yup.string().nullable(),
});

export type CollabAgreementFormValues = {
  collaborationAgreement: File | null;
  expirationDate: string;
  physicianFirstName: string;
  physicianLastName: string;
  physicianNpi: string;
  physicianEmail: string;
  physicianPhone: string;
};

export const collabAgreementDefaultValues: CollabAgreementFormValues = {
  collaborationAgreement: null,
  expirationDate: '',
  physicianFirstName: '',
  physicianLastName: '',
  physicianNpi: '',
  physicianEmail: '',
  physicianPhone: '',
};

const CollabAgreementDropzone: React.VFC<{ name: string; label: string }> = ({ name, label }) => {
  const [ field, meta, helpers ] = useField<File | null>(name);

  return (
    <Box display="flex" flexDirection="column">
      <InputLabel shrink htmlFor={name} error={Boolean(meta.touched && meta.error)} sx={{ mb: 0.5 }}>
        {label}
      </InputLabel>
      <SimpleDropzone
        maxSize={MAX_PDF_UPLOAD_SIZE}
        accept={{ 'application/pdf': [ '.pdf' ] }}
        maxFiles={1}
        onDrop={(accepted) => {
          if (accepted[0]) helpers.setValue(accepted[0]);
        }}
        color="secondary"
        error={Boolean(meta.touched && meta.error)}
      >
        {() => (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            py={2}
            px={3}
          >
            {field.value ? (
              <>
                <Article color="secondary" />
                <Typography variant="body2" color="secondary">
                  {(field.value as File).name}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="inherit">
                Click or drag a PDF file here
              </Typography>
            )}
          </Box>
        )}
      </SimpleDropzone>
      {meta.touched && meta.error && <FormHelperText error={!!meta.error}>{meta.error}</FormHelperText>}
    </Box>
  );
};

export const CollabAgreementForm: React.VFC = () => {
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <CollabAgreementDropzone name="collaborationAgreement" label="Upload Collaboration Agreement PDF" />

      <DatePicker
        name="expirationDate"
        label="Expiration Date"
        fullWidth
        valueFormat="MM/DD/YYYY"
        color="secondary"
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput
            name="physicianFirstName"
            label="Physician First Name"
            fullWidth
            color="secondary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextInput
            name="physicianLastName"
            label="Physician Last Name"
            fullWidth
            color="secondary"
          />
        </Grid>
      </Grid>

      <MaskedInput
        name="physicianNpi"
        label="Collaborating Physician NPI"
        type="npi"
        fullWidth
        color="secondary"
      />

      <TextInput
        name="physicianEmail"
        label="Collaborating Physician Email"
        fullWidth
        color="secondary"
      />

      <MaskedInput
        name="physicianPhone"
        label="Collaborating Physician Phone"
        type="phone"
        fullWidth
        color="secondary"
      />
    </Box>
  );
};
